// styles
import styles from '../detail.module.scss';

// types
import type { ProductLocatorItem } from 'components/ContentElementsGdds/locator/showroom/sidebar';

// components
import { InnerHtml } from 'components/InnerHtml/inner-html';
import { Translation } from 'components/Translation/Translation';
import { ExternalLink } from 'components/Link/ExternalLink';

// utils
import { externalLinkProps } from 'components/ContentElementsGdds/locator/showroom';
import { classNameBuilder } from 'utils/classNameBuilder';
import { decodingContent } from 'utils/decodingContent';
import { useTranslationFunction } from 'utils/hooks/use-translations';
import { isEmpty } from 'utils/is-empty';

type ProductDetailProps = {
  item: ProductLocatorItem;
  detailLayer?: string;
  openAcc?: boolean;
  isAcc?: boolean;
  isNord?: boolean;
};

export function ProductDetail({
  detailLayer,
  item: {
    name,
    address,
    fax,
    phone,
    email,
    website,
    zip_location: zipLocation,
    description_title: descriptionHeadline,
    description: descriptionBody,
  },
  openAcc = false,
  isAcc = false,
  isNord = false,
}: Readonly<ProductDetailProps>) {
  const translate = useTranslationFunction();
  const openClass = openAcc ? styles.open : styles.closed;
  const nordProps = {
    stylingType: 'primary' as const,
    alignByContent: 'left' as const,
    standardFontSize: false,
  };

  const setWrapper = (children) => {
    if (isAcc) {
      return (
        <div className={classNameBuilder(openClass, isNord && styles.nord)}>
          <div className={classNameBuilder(styles.sidebarAccWrapper, isNord && styles.nord)}>
            {children}
          </div>
        </div>
      );
    }

    return children;
  };

  return setWrapper(
    <div className={classNameBuilder(styles.locatorDetail, isNord && styles.nord)}>
      <ul>
        {!isEmpty(detailLayer) && !isEmpty(name) && (
          <li>
            <InnerHtml as="b" content={name} />
          </li>
        )}
        {!isEmpty(address) && !(isAcc && isNord) && <InnerHtml as="li" content={address} />}
        {!isEmpty(zipLocation) && !(isAcc && isNord) && <InnerHtml as="li" content={zipLocation} />}
        {!isEmpty(phone) && (
          <li>
            <Translation id="web20_locator_phone" />: {decodingContent(phone)}
          </li>
        )}
        {!isEmpty(fax) && (
          <li>
            <Translation id="web20_locator_fax" />
            {decodingContent(fax)}
          </li>
        )}
        {!isEmpty(email) && (
          <li>
            <ExternalLink
              content={{
                ...externalLinkProps,
                target: `mailto:${email}`,
                text: translate('web20_locator_email'),
                showArrow: true,
              }}
              pure
              {...(isNord && nordProps)}
            />
          </li>
        )}
        {!isEmpty(website) && (
          <li>
            <ExternalLink
              content={{
                ...externalLinkProps,
                target: website,
                text: translate('web20_locator_website'),
                showArrow: true,
              }}
              pure
              {...(isNord && nordProps)}
            />
          </li>
        )}
      </ul>
      {!(isAcc && isNord) && (
        <div className="locator__detail">
          {!isEmpty(descriptionHeadline) && (
            <li>
              <InnerHtml as="b" content={descriptionHeadline} />
            </li>
          )}
          {!isEmpty(descriptionBody) && <InnerHtml as="li" content={descriptionBody} />}
        </div>
      )}
    </div>,
  );
}
